import {
  CheckCheckIcon,
  ForwardIcon,
  GitPullRequestClosed,
  Loader,
  LocateIcon,
  Route,
  XIcon,
} from "lucide-react";
import { useEffect, useState } from "react";

import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogPanel, DialogTitle } from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Spinner } from "@/components/ui/spinner";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import { cn } from "@/lib/utils";

import { useComputedItineraries, useInitIncident } from "../../api/use-claim-actions";
import { useConfirmIncident, useExtractIncidentAi } from "../../api/use-claim-insights";
import type { AiFlightEntry, AiIncidentData } from "../../lib/incident-delta";
import {
  AiIncidentPreview,
  type AiIncidentPreviewConfirmData,
  CAUSE_OPTIONS,
  CONSEQUENCE_OPTIONS,
  type IncidentCause,
  type IncidentConsequence,
} from "../ai-insights/ai-incident-preview";
import { ComputedFlightCard } from "./computed-flight-card";
import {
  buildInitIncidentPayload,
  type ComputedItineraryFlight,
  type FlightSelectionKey,
  getSequenceLabel,
  groupImpactedBySequence,
  type SelectedFlights,
} from "./init-incident-logic";

/**
 * « Initialiser l'incident » — manual incident editor. Port of the TopMenu.vue
 * incident AlertModal + incidents/InitIncident.vue, with the AI fallback
 * (handleIncidentFallbackToAi → AiIncidentPreview → postIncidentAi) when the
 * computed itineraries cannot be resolved.
 */

const FLIGHT_SELECTION_LABELS: Record<FlightSelectionKey, { button: string; tooltip: string }> = {
  faultyFlight: { button: "Vol fautif", tooltip: "Vol fautif" },
  impactedFlight: { button: "Vol impacté", tooltip: "Vol impacté" },
  jurisdictionalFlight: { button: "Vol attaqué", tooltip: "Vol attaqué" },
};

const FLIGHT_SELECTION_KEYS: FlightSelectionKey[] = [
  "faultyFlight",
  "impactedFlight",
  "jurisdictionalFlight",
];

interface InitIncidentEditorProps {
  claimId: string;
  enabled: boolean;
  onIncidentInitialized: () => void;
  onFallbackToAi: () => void;
}

/** Port of incidents/InitIncident.vue (comparaison d'itinéraire + confirmation). */
function InitIncidentEditor({
  claimId,
  enabled,
  onIncidentInitialized,
  onFallbackToAi,
}: InitIncidentEditorProps) {
  const { data: computedIncident, isLoading } = useComputedItineraries(claimId, enabled);
  const initIncident = useInitIncident(claimId);

  const isSuccess = Boolean(computedIncident && "original" in computedIncident && computedIncident.original);

  // Mirrors the Vue watcher: no computable itinerary → fall back to the AI extraction.
  useEffect(() => {
    if (computedIncident && !isSuccess) {
      onFallbackToAi();
    }
  }, [computedIncident, isSuccess, onFallbackToAi]);

  const [selectedCause, setSelectedCause] = useState<IncidentCause | null>(null);
  const [selectedConsequence, setSelectedConsequence] = useState<IncidentConsequence | null>(null);
  const [selectedFlights, setSelectedFlights] = useState<SelectedFlights>({});
  const [flightSelectionType, setFlightSelectionType] = useState<FlightSelectionKey | undefined>();

  if (isLoading || !computedIncident || !isSuccess) {
    return (
      <div className="flex items-center justify-center gap-2 py-16 text-sm text-slate-500">
        <Spinner /> Calcul des itinéraires en cours…
      </div>
    );
  }

  const cause: IncidentCause = selectedCause ?? computedIncident.computedCause ?? CAUSE_OPTIONS[0].value;
  const consequenceNotFound = !selectedConsequence && !computedIncident.computedConsequence;
  const consequence: IncidentConsequence =
    selectedConsequence ?? computedIncident.computedConsequence ?? CONSEQUENCE_OPTIONS[0].value;

  const groupedOriginal = groupImpactedBySequence(computedIncident.original);
  const groupedActual = groupImpactedBySequence(computedIncident.actual);
  const originalCount = computedIncident.original.filter(
    ({ isImpactedSequence }) => isImpactedSequence,
  ).length;
  const actualCount = computedIncident.actual.filter(({ isImpactedSequence }) => isImpactedSequence).length;

  // Mirrors handleFlightSelection (Vue).
  function handleFlightSelection(flight: ComputedItineraryFlight) {
    if (!flightSelectionType) {
      return;
    }
    setSelectedFlights((previous) => ({ ...previous, [flightSelectionType]: flight }));
    setFlightSelectionType(undefined);
  }

  function toggleFlightSelection(key: FlightSelectionKey) {
    if (flightSelectionType === key) {
      setFlightSelectionType(undefined);
      setSelectedFlights((previous) => ({ ...previous, [key]: undefined }));
    } else {
      setFlightSelectionType(key);
    }
  }

  // Mirrors initIncident (Vue).
  function submitIncident() {
    if (!computedIncident) {
      return;
    }
    initIncident.mutate(buildInitIncidentPayload({ computedIncident, selectedFlights, cause, consequence }), {
      onSuccess: ({ success }) => {
        if (success) {
          onIncidentInitialized();
        }
      },
    });
  }

  return (
    <div className="min-h-full bg-gradient-to-br from-slate-50 to-blue-50 p-4">
      <div className="mx-auto max-w-7xl">
        {/* Header */}
        <div className="mb-8 text-center">
          <h1 className="mb-2 text-3xl font-bold text-slate-800">Comparaison d'Itinéraire</h1>
          <p className="text-base text-slate-600">Itinéraire prévu vs effectué</p>
        </div>

        {/* Comparison Grid */}
        <div className="grid gap-8 px-4 lg:grid-cols-2">
          {/* Original Itinerary */}
          <div className="space-y-6">
            <div className="mb-6 flex items-center gap-3">
              <div className="h-3 w-3 rounded-full bg-blue-500" />
              <h2 className="text-xl font-semibold text-slate-800">Réservation prévue</h2>
              <span className="rounded-full bg-blue-100 px-3 py-1 text-sm font-medium text-blue-700">
                {originalCount} vols
              </span>
            </div>

            {groupedOriginal.map(([sequenceNum, sequence]) => (
              <div key={sequenceNum} className="space-y-4">
                <div className="mb-4 flex items-center gap-2">
                  <div className="flex h-8 w-8 items-center justify-center rounded-full bg-blue-500 text-sm font-bold text-white">
                    {sequenceNum}
                  </div>
                  <span className="font-medium text-slate-700">{getSequenceLabel(sequence[0].sequence)}</span>
                </div>

                <div
                  className={cn(
                    "overflow-hidden rounded-2xl border-8 bg-white shadow-sm",
                    flightSelectionType ? "border-blue-200 shadow-md" : "border-red-600",
                  )}
                >
                  {sequence.map((entry, index) => (
                    <ComputedFlightCard
                      key={entry.flight.id}
                      entry={entry}
                      type="original"
                      isLast={index === sequence.length - 1}
                      selectable={Boolean(flightSelectionType)}
                      selected={FLIGHT_SELECTION_KEYS.some(
                        (key) => selectedFlights[key]?.id === entry.flight.id,
                      )}
                      onSelect={() => handleFlightSelection(entry.flight)}
                    />
                  ))}
                </div>
              </div>
            ))}
          </div>

          {/* Actual Itinerary */}
          <div className="space-y-6">
            <div className="mb-6 flex items-center gap-3">
              <div className="h-3 w-3 rounded-full bg-amber-500" />
              <h2 className="text-xl font-semibold text-slate-800">Itinéraire Final</h2>
              <span className="rounded-full bg-amber-100 px-3 py-1 text-sm font-medium text-amber-700">
                {actualCount} vols
              </span>
            </div>

            {groupedActual.map(([sequenceNum, sequence]) => (
              <div key={sequenceNum} className="space-y-4">
                <div className="mb-4 flex items-center gap-2">
                  <div className="flex h-8 w-8 items-center justify-center rounded-full bg-amber-500 text-sm font-bold text-white">
                    {sequenceNum}
                  </div>
                  <span className="font-medium text-slate-700">{getSequenceLabel(sequence[0].sequence)}</span>
                </div>

                <div className="overflow-hidden rounded-2xl border-8 border-blue-200 bg-white shadow-md">
                  {sequence.map((entry, index) => (
                    <ComputedFlightCard
                      key={entry.flight.id}
                      entry={entry}
                      type="actual"
                      isLast={index === sequence.length - 1}
                    />
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Confirmer l'incident */}
        <div className="mt-8 rounded-2xl border border-slate-200 bg-white p-6 shadow-sm">
          <div className="mb-4 flex items-center gap-3">
            <GitPullRequestClosed className="h-5 w-5 text-amber-500" />
            <h3 className="text-lg font-semibold text-slate-800">Confirmer l'incident</h3>
          </div>

          <div className="grid gap-4 md:grid-cols-2">
            <div className="rounded-xl bg-blue-50 p-4 text-center">
              <div className="text-2xl font-bold text-slate-800">1</div>
              <div className="text-left">
                <Label className="mb-1 block text-sm font-medium text-gray-700">Sélectionner la cause</Label>
                <Select
                  value={cause}
                  onValueChange={(value) => {
                    if (value) {
                      setSelectedCause(value as IncidentCause);
                    }
                  }}
                >
                  <SelectTrigger className="w-full bg-white">
                    <SelectValue>{CAUSE_OPTIONS.find((option) => option.value === cause)?.label}</SelectValue>
                  </SelectTrigger>
                  <SelectContent>
                    {CAUSE_OPTIONS.map((option) => (
                      <SelectItem key={option.value} value={option.value}>
                        {option.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Multi-leg flight role selection (faulty / impacted / jurisdictional) */}
              {computedIncident.isMultiLeg ? (
                <div className="mt-8 flex space-x-2">
                  {FLIGHT_SELECTION_KEYS.map((key) => {
                    const selectedFlight = selectedFlights[key];
                    const button = (
                      <Button
                        variant={selectedFlight ? "secondary" : "default"}
                        onClick={() => toggleFlightSelection(key)}
                      >
                        {flightSelectionType === key ? (
                          <XIcon />
                        ) : !selectedFlight ? (
                          <LocateIcon />
                        ) : (
                          <CheckCheckIcon />
                        )}
                        {selectedFlight
                          ? selectedFlight.flightIdentifier
                          : FLIGHT_SELECTION_LABELS[key].button}
                      </Button>
                    );
                    if (!selectedFlight) {
                      return <div key={key}>{button}</div>;
                    }
                    return (
                      <Tooltip key={key}>
                        <TooltipTrigger render={<span />}>{button}</TooltipTrigger>
                        <TooltipContent>
                          {`${FLIGHT_SELECTION_LABELS[key].tooltip} (${selectedFlight.departureAirport?.iata} → ${selectedFlight.arrivalAirport?.iata})`}
                        </TooltipContent>
                      </Tooltip>
                    );
                  })}
                </div>
              ) : null}
            </div>

            <div className="rounded-xl bg-emerald-50 p-4 text-center">
              <div className="text-2xl font-bold text-emerald-600">2</div>
              <div className="text-left">
                <Label className="mb-1 block text-sm font-medium text-gray-700">Type de conséquence</Label>
                {consequenceNotFound ? (
                  <p className="mb-1 text-sm text-slate-600">
                    ⚠️ Aucune conséquence trouvée automatiquement ⚠️
                  </p>
                ) : null}
                <Select
                  value={consequence}
                  onValueChange={(value) => {
                    if (value) {
                      setSelectedConsequence(value as IncidentConsequence);
                    }
                  }}
                >
                  <SelectTrigger className="w-full bg-white">
                    <SelectValue>
                      {CONSEQUENCE_OPTIONS.find((option) => option.value === consequence)?.label}
                    </SelectValue>
                  </SelectTrigger>
                  <SelectContent>
                    {CONSEQUENCE_OPTIONS.map((option) => (
                      <SelectItem key={option.value} value={option.value}>
                        {option.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
          </div>

          <div className="mb-8 mt-12 flex justify-center">
            <Button size="lg" onClick={submitIncident} disabled={initIncident.isPending}>
              {initIncident.isPending ? <Loader className="animate-spin" /> : <ForwardIcon />}
              Confirmer l'incident
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}

/** Loading shell of the AI extraction — port of the TopMenu.vue fallback markup. */
function AiExtractionLoading() {
  return (
    <div className="flex flex-col items-center justify-center py-16 text-center">
      <div className="relative mb-6 h-9 w-20">
        <div className="absolute bottom-px left-1/2 flex size-9 origin-bottom-left -translate-x-0.5 -rotate-[10deg] scale-[0.84] items-center justify-center rounded-md border bg-white text-zinc-400">
          <Loader className="h-[18px] w-[18px] animate-spin" />
        </div>
        <div className="absolute bottom-px left-1/2 flex size-9 origin-bottom-right translate-x-0.5 rotate-[10deg] scale-[0.84] items-center justify-center rounded-md border bg-white text-zinc-400">
          <Loader className="h-[18px] w-[18px] animate-spin" />
        </div>
        <div className="relative left-1/2 flex size-9 -translate-x-1/2 items-center justify-center rounded-md border bg-white text-zinc-700 shadow-sm">
          <Route className="h-[18px] w-[18px]" />
        </div>
      </div>
      <h3 className="text-xl font-semibold text-zinc-900">Analyse de l'incident en cours</h3>
      <p className="mt-1 max-w-sm text-sm text-zinc-500">
        L'IA structure l'incident à partir du PNR et des données de vol.
      </p>
    </div>
  );
}

interface InitIncidentModalProps {
  claimId: string;
  open: boolean;
  onClose: () => void;
}

export function InitIncidentModal({ claimId, open, onClose }: InitIncidentModalProps) {
  const [aiFallback, setAiFallback] = useState(false);
  const [aiData, setAiData] = useState<AiIncidentData | null>(null);

  const extractIncidentAi = useExtractIncidentAi(claimId);
  const confirmIncident = useConfirmIncident(claimId);

  function handleClose() {
    setAiFallback(false);
    setAiData(null);
    onClose();
  }

  // Mirrors handleIncidentFallbackToAi (Vue).
  function handleFallbackToAi() {
    if (aiFallback) {
      return;
    }
    setAiFallback(true);
    extractIncidentAi.mutate(undefined, {
      onSuccess: (metadata) => setAiData(metadata),
      onError: () => handleClose(),
    });
  }

  // Mirrors confirmAiIncidentCreation (Vue) + the sequence override of InitIncidentWorkflow.
  function handleAiConfirm({ cause, consequence, sequence }: AiIncidentPreviewConfirmData) {
    if (!aiData) {
      return;
    }
    const markImpacted = (flights?: AiFlightEntry[] | null) =>
      (flights ?? []).map((flight) => ({ ...flight, isImpactedSequence: flight.sequence === sequence }));
    confirmIncident.mutate(
      {
        incident: {
          ...aiData.incident,
          cause,
          consequence,
          itinerary: {
            original: markImpacted(aiData.incident.itinerary?.original),
            actual: markImpacted(aiData.incident.itinerary?.actual),
          },
        },
        etickets: aiData.etickets ?? [],
      },
      {
        onSuccess: ({ success }) => {
          if (success) {
            handleClose();
          }
        },
      },
    );
  }

  return (
    <Dialog
      open={open}
      onOpenChange={(isOpen) => {
        if (!isOpen) {
          handleClose();
        }
      }}
    >
      <DialogContent className="sm:max-w-5xl">
        <DialogHeader>
          <DialogTitle>Confirmer l'incident</DialogTitle>
        </DialogHeader>
        <DialogPanel>
          {aiFallback && aiData ? (
            <AiIncidentPreview
              incident={aiData}
              showValidate
              loading={confirmIncident.isPending}
              onConfirm={handleAiConfirm}
            />
          ) : aiFallback ? (
            <AiExtractionLoading />
          ) : (
            <InitIncidentEditor
              claimId={claimId}
              enabled={open}
              onIncidentInitialized={handleClose}
              onFallbackToAi={handleFallbackToAi}
            />
          )}
        </DialogPanel>
      </DialogContent>
    </Dialog>
  );
}
